import { useState } from "react";
import { Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import DefaultSpinner from "../../../components/LoadingSpinner/default-spinner.component";
import postUserPhoto, { deleteUserPhoto } from "../functions/postUserPhoto";
import { PictureDropzone2 } from "../../../components/Pickers/picture-dropzone.component(2)";
import { FileObject } from "../../FileDropzone/typings/FileObject.type";

type P = {
  handleClose: () => void;
};

/**
 * Connects to redux and renders a file dropzone to upload a user image.
 * Accepts images only. Will show the existing user photo. Upload is attempted as soon
 * as picture is dropped (DnD) or selected (file explorer).
 */
const UserImageUpload: React.FC<P> = ({ handleClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const photoUrl = useSelector((state: RootState) => state.login.userInformation.photoUrl);

  const [files, setFiles] = useState<FileObject[]>([]);

  const { isLoading, isSuccess, mutate } = useMutation(postUserPhoto, {
    onSuccess: () => globalThis.location.reload(),
    onSettled: () => setFiles([]),
    onError: () => {
      enqueueSnackbar("There was an error uploading your photo. Please select a different one.", {
        variant: "error"
      });
    }
  });
  const deleteMut = useMutation(deleteUserPhoto, {
    onSuccess: () => globalThis.location.reload()
  });

  return (
    <>
      <PictureDropzone2
        title={
          photoUrl
            ? t("Change your existing profile picture")
            : t("Upload a picture for your profile")
        }
        files={files}
        current={photoUrl}
        handleClose={handleClose}
        onAdd={files => {
          if (files.length) {
            setFiles(files);
            mutate(files[0].file);
          }
        }}
        onDelete={(_, index) => setFiles(prev => prev.filter((_, i) => i !== index))}
      />
      {photoUrl && (
        <Button data-testid="delete-profile-pic" onClick={() => deleteMut.mutate()}>
          {t("Delete my profile picture")}
        </Button>
      )}

      {(isLoading || deleteMut.isLoading) && (
        <DefaultSpinner size={30} style={{ padding: "10px" }} />
      )}
      {isSuccess && <Typography>{t("Image uploaded! Refreshing...")}</Typography>}
      {deleteMut.isSuccess && <Typography>{t("Image deleted! Refreshing...")}</Typography>}
    </>
  );
};

export default UserImageUpload;
